import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

/**
 * UnDosTres brand mark with a "Cards / Tarjetas" product descriptor.
 *
 * Composition:
 *   [undostres wordmark] | [Cards | Tarjetas]
 *
 * Props:
 *   variant   "light" (default — header on white) | "dark" (footer on dark)
 *   size      "md" (default) | "sm"
 *   to        if provided, wraps the mark in a <Link>
 */
const Logo = ({ variant = "light", size = "md", to }) => {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || "es").toLowerCase().startsWith("en") ? "en" : "es";
  const descriptor = lang === "en" ? "Cards" : "Tarjetas";

  const dims =
    size === "sm"
      ? { wordmark: 110, descriptor: 12, gap: 8, divider: 18 }
      : { wordmark: 150, descriptor: 14, gap: 12, divider: 22 };

  const isDark = variant === "dark";
  const wordmarkStyle = {
    height: `${(dims.wordmark * 259) / 1200}px`,
    width: `${dims.wordmark}px`,
    objectFit: "contain",
    display: "block"
  };

  // On dark surfaces we frame the wordmark with a light pill so the white
  // background of the JPG looks intentional rather than accidental.
  const wordmarkWrap = isDark
    ? {
        padding: "6px 10px",
        background: "#ffffff",
        borderRadius: "10px",
        display: "inline-flex",
        alignItems: "center"
      }
    : { display: "inline-flex", alignItems: "center" };

  const dividerStyle = {
    width: "1px",
    height: `${dims.divider}px`,
    background: isDark ? "rgba(255,255,255,0.22)" : "rgba(15,23,42,0.18)"
  };

  const descriptorStyle = {
    fontSize: `${dims.descriptor}px`,
    fontWeight: 700,
    letterSpacing: "0.10em",
    textTransform: "uppercase",
    color: isDark ? "#ffffff" : "#0b3d6b",
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
  };

  const wrapperStyle = {
    display: "inline-flex",
    alignItems: "center",
    gap: `${dims.gap}px`,
    textDecoration: "none",
    color: "inherit"
  };

  const content = (
    <span style={wrapperStyle} data-testid="brand-logo">
      <span style={wordmarkWrap}>
        <img
          src="/undostres-logo.jpg"
          alt={`${t("brand.name")} — ${descriptor}`}
          style={wordmarkStyle}
        />
      </span>
      <span style={dividerStyle} aria-hidden="true" />
      <span style={descriptorStyle} data-testid="brand-descriptor">
        {descriptor}
      </span>
    </span>
  );

  if (to) {
    return (
      <Link to={to} style={{ textDecoration: "none" }}>
        {content}
      </Link>
    );
  }
  return content;
};

export default Logo;
